import { useApi } from '@/api';
import { ChartSkeleton } from '@/components';
import { useChartTooltip } from '@/hooks';
import {
  Flex,
  formatPercentage,
  textShortener,
  DoughnutChart
} from '@blockanalitica/ui';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateGradientColor } from '@/utils';
import { ILKS_PALETTE } from '@/constants';

export default function CdpDebtCompositionDonutChart() {
  const [data, error] = useApi({
    url: '/at-risk/share/cdps/'
  });

  if (error) {
    throw error;
  }

  const navigate = useNavigate();
  const customTooltip = useChartTooltip(
    data,
    'cdp',
    'share',
    true,
    'debt_at_risk',
    'ilk'
  );

  const chartData = useMemo(() => {
    if (data) {
      // Get unique ILKs and count CDPs per ILK
      const ilkCdpCount = data.reduce((acc, row) => {
        if (!acc[row.ilk]) {
          acc[row.ilk] = 0;
        }
        acc[row.ilk]++;
        return acc;
      }, {});
      return {
        labels: data.map((row) => textShortener(row.cdp)),
        datasets: [
          {
            data: data.map((row) => row.share),
            hoverOffset: 30,
            backgroundColor: data.map((row, index) => {
              const baseColor = ILKS_PALETTE[row.ilk] || '#000000';
              const cdpIndex = index % ilkCdpCount[row.ilk];
              const totalCdps = ilkCdpCount[row.ilk];
              return generateGradientColor(baseColor, cdpIndex, totalCdps);
            }),
            borderColor: data.map((row, index) => {
              const baseColor = ILKS_PALETTE[row.ilk] || '#000000';
              const cdpIndex = index % ilkCdpCount[row.ilk];
              const totalCdps = ilkCdpCount[row.ilk];
              return generateGradientColor(baseColor, cdpIndex, totalCdps);
            })
          }
        ]
      };
    }
    return null;
  }, [data]);

  const mapping = useMemo(() => {
    if (data) {
      return data.reduce((acc, row) => {
        const key = textShortener(row.cdp);
        acc[key] =
          key === 'others' ? `${row.ilk}/cdps` : `${row.ilk}/cdps/${row.urn}`;
        return acc;
      }, {});
    }
    return {};
  }, [data]);

  const customConfig = useMemo(
    () => ({
      options: {
        aspectRatio: 1.8,
        layout: {
          padding: 10
        },
        plugins: {
          legend: {
            position: 'bottom'
          },
          tooltip: {
            enabled: false,
            position: 'nearest',
            external: customTooltip
          }
        }
      }
    }),
    [customTooltip]
  );

  if (data) {
    return (
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        paddingTop="m"
        width="100%">
        <DoughnutChart
          // @ts-ignore
          config={customConfig}
          data={chartData}
          valueFormatter={formatPercentage}
          url={`/collateral/core`}
          navigate={navigate}
          mapping={mapping}
        />
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      paddingTop="m"
      width="100%">
      <ChartSkeleton type="doughnut" />
    </Flex>
  );
}
